/**
 * Headers are now loaded from backend only (OTHeatmapHeaders model)
 * No default values in frontend - always load from backend
 * 
 * Assessment Dimensions are dynamically mapped from headerValues array using sortOrder. These are populated by the service when loading data with headers. For example, if sortOrder is 1, then the first element of headerValues array is the value for the first assessment dimension.
 * 
 * Assessment Dimensions keys are used for images. so fitForPurpose.png is the image for fitForPurpose dimension.
 * Even for Decision, the key is used for image. so Sustain.png is the image for Sustain decision.
 * 
 * */

export interface IOTHeatmap {
  _id?: string;
  assetName?: string;
  assetType?: string;
  manufacturer?: string;

  // Decision
  decision?: string;

  // Raw headerValues array from backend (sortOrder-1 as index)
  headerValues?: string[];

  // Index signature to allow dynamic property access
  [key: string]: any;
}

export interface IOTHeatmapHeader {
  _id?: string;
  key: string;
  label: string;
  sortOrder: number;
}

/**
 * Consolidated concern level configuration with all properties
 */
export const OT_CONCERN_LEVELS_CONFIG: {
  [key: string]: {
    iconClass: string;
    buttonClass: string;
    backgroundColor: string;
    textColor: string;
  }
} = {
  'Not Assessed': {
    iconClass: 'bright-secondary',
    buttonClass: 'btn-secondary2 text-dark empty-border',
    backgroundColor: '#c3c3c3',
    textColor: 'text-dark'
  },
  'Severe Concern': {
    iconClass: 'dark-red',
    buttonClass: 'btn-danger1 text-white',
    backgroundColor: '#8d1101',
    textColor: 'text-white'
  },
  'Significant Concern': {
    iconClass: 'bright-red',
    buttonClass: 'bright-red text-white',
    backgroundColor: '#d90606',
    textColor: 'text-white'
  },
  'Moderate Concern': {
    iconClass: 'yellow',
    buttonClass: 'btn-warning1',
    backgroundColor: '#FFBF00',
    textColor: ''
  },
  'No Concern': {
    iconClass: 'bright-green',
    buttonClass: 'btn-success1',
    backgroundColor: '#4dc013',
    textColor: ''
  },
  'Not Applicable': {
    iconClass: 'light-secondary',
    buttonClass: 'btn-light1 text-dark',
    backgroundColor: '#ededed',
    textColor: 'text-dark'
  }
};

/**
 * Get CSS class name for concern level icons/display elements
 * Used for ngClass binding in components (icons, color boxes, etc.)
 */
export function getConcernClassName(concernName: string | undefined | null): string {
  return concernName && OT_CONCERN_LEVELS_CONFIG[concernName] ? OT_CONCERN_LEVELS_CONFIG[concernName]?.iconClass : OT_CONCERN_LEVELS_CONFIG['Not Assessed'].iconClass;
}

/**
 * Get background color for inline styles (used in otdata component)
 */
export function getConcernBackgroundColor(concernName: string | undefined | null): { [key: string]: string } {
  const config = (concernName && OT_CONCERN_LEVELS_CONFIG[concernName]) || OT_CONCERN_LEVELS_CONFIG['Not Assessed'];
  return { 'background-color': config.backgroundColor };
}

/**
 * Get CSS classes for buttons/controls in heatmap component
 * Used for button styling in input components
 */
export function getConcernCssClassesInput(concernName: string | undefined | null): string {
  const config = (concernName && OT_CONCERN_LEVELS_CONFIG[concernName]) || OT_CONCERN_LEVELS_CONFIG['Not Assessed'];
  return config.buttonClass;
}

/**
 * Get decision image path
 */
export function getDecisionImage(name?: string) {
  return name ? `assets/images/uploads/arrows/${name}.png` : OT_DECISIONS[0].image;
}

/**
 * Array format for backward compatibility (derived from consolidated config)
 * In backend also saving the same name itself - so directly using the name
 */
export const OT_CONCERN_LEVELS = Object.keys(OT_CONCERN_LEVELS_CONFIG).map(name => ({
  name,
  className: OT_CONCERN_LEVELS_CONFIG[name].iconClass
}));

export const HIGH_CONCERN_NAMES = ['Severe Concern', 'Significant Concern'];

// In backend also saving the same name itself
export const OT_DECISIONS = [
  { name: 'Undecided', image: '/assets/images/uploads/arrows/Undecided.png' },
  { name: 'Sustain', image: '/assets/images/uploads/arrows/Sustain.png' },
  { name: 'Freeze', image: '/assets/images/uploads/arrows/Freeze.png' },
  { name: 'Upgrade', image: '/assets/images/uploads/arrows/Upgrade.png' },
  { name: 'Replace', image: '/assets/images/uploads/arrows/Replace.png' },
  { name: 'Retire', image: '/assets/images/uploads/arrows/Retire.png' },
  { name: 'Merge', image: '/assets/images/uploads/arrows/Merge.png' },
  { name: 'Tolerate', image: '/assets/images/uploads/arrows/Tolerate.png' }
];
